import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Html, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { ALL_MUSCLE_IDS, MUSCLES, type MuscleId } from "@/entities/muscle/model/muscles";
import type { MuscleHighlightMap } from "@/entities/exercise/model/exercise-types";
import type { ExerciseId } from "@/shared/domain/ids";
import { getAnimation } from "@/entities/exercise/model/animations";
import { cn } from "@/shared/lib/utils";

/**
 * AnatomyModel — loads a Z-Anatomy GLB, highlights muscles by name, and
 * animates limbs by reparenting meshes under joint pivots.
 */

type MuscleClassification =
  | { kind: "muscle"; muscleId: MuscleId }
  | { kind: "generic" }
  | { kind: "hidden" };

const MUSCLE_NAME_PATTERNS: Readonly<Record<MuscleId, ReadonlyArray<RegExp>>> = {
  chest: [/pectoralis/i],
  back: [/latissimus[_\s]dorsi/i, /rhomboid/i, /\bteres\b/i, /infraspinatus/i, /supraspinatus/i],
  traps: [/trapezius/i, /levator[_\s]scapulae/i],
  shoulders: [/deltoid/i],
  biceps: [/biceps[_\s]brachii/i, /brachialis(?![A-Za-z])/i, /coracobrachialis/i],
  triceps: [/triceps[_\s]brachii/i, /\banconeus\b/i],
  forearms: [
    /brachioradialis/i,
    /flexor[_\s]carpi/i,
    /extensor[_\s]carpi/i,
    /flexor[_\s]digitorum/i,
    /extensor[_\s]digitorum/i,
    /pronator/i,
    /supinator/i,
  ],
  abs: [/rectus[_\s]abdominis/i],
  obliques: [
    /abdominal[_\s]oblique/i,
    /external[_\s]oblique/i,
    /internal[_\s]oblique/i,
    /transversus[_\s]abdominis/i,
  ],
  glutes: [/gluteus[_\s](maximus|medius|minimus)/i],
  quads: [/rectus[_\s]femoris/i, /vastus[_\s](lateralis|medialis|intermedius)/i],
  hamstrings: [/biceps[_\s]femoris/i, /semitendinosus/i, /semimembranosus/i],
  calves: [/gastrocnemius/i, /\bsoleus\b/i, /tibialis[_\s]posterior/i],
};

const GENERIC_MUSCLE_PATTERN = /\bmuscle\b|muscul/i;

const HIDE_PATTERNS: ReadonlyArray<RegExp> = [
  /\bbone\b/i,
  /skeleton/i,
  /vertebra/i,
  /\brib\b/i,
  /\bskull\b/i,
  /\bcrani/i,
  /\bjoint\b/i,
  /cartilage/i,
  /\bdisc\b/i,
  /\bdisk\b/i,
  /ligament/i,
  /tendon/i,
  /aponeurosis/i,
  /fascia/i,
  /retinaculum/i,
  /artery/i,
  /\bvein\b/i,
  /vena\b/i,
  /vessel/i,
  /capillary/i,
  /nerve/i,
  /ganglion/i,
  /plexus/i,
  /lymph/i,
  /\bnode\b/i,
  /thymus/i,
  /spleen/i,
  /heart/i,
  /atrium/i,
  /ventricle/i,
  /aorta/i,
  /lung/i,
  /bronch/i,
  /trache/i,
  /pharynx/i,
  /larynx/i,
  /epiglott/i,
  /esophag/i,
  /stomach/i,
  /intestin/i,
  /liver/i,
  /pancreas/i,
  /gallbladder/i,
  /kidney/i,
  /bladder/i,
  /ureter/i,
  /urethra/i,
  /uterus/i,
  /ovary/i,
  /testis/i,
  /prostate/i,
  /penis/i,
  /vagina/i,
  /scrotum/i,
  /\beye\b/i,
  /eyeball/i,
  /\bear\b/i,
  /cochlea/i,
  /malleus/i,
  /incus/i,
  /stapes/i,
  /tongue/i,
  /\btooth\b/i,
  /teeth/i,
  /palate/i,
  /gland/i,
  /salivary/i,
  /thyroid/i,
  /parathyroid/i,
  /metacarpal/i,
  /metatarsal/i,
  /phalanx/i,
  /phalanges/i,
  /\bcarpal/i,
  /\btarsal/i,
  /femur/i,
  /tibia/i,
  /fibula/i,
  /humerus/i,
  /radius/i,
  /ulna/i,
  /scapula/i,
  /clavicle/i,
  /pelvis/i,
  /sacrum/i,
  /\bilium\b/i,
  /ischium/i,
  /pubis/i,
  /patella/i,
  /sternum/i,
  /hyoid/i,
  /brain/i,
  /cerebrum/i,
  /cerebell/i,
  /\bspinal\b/i,
  /medulla/i,
  /\bpons\b/i,
];

type LimbSide = "l" | "r";
type LimbSegment = "upperarm" | "forearm" | "hand" | "thigh" | "shin" | "foot";

type MeshUserData = {
  classification?: MuscleClassification;
  clonedMaterial?: THREE.Material;
};

const SKIN_COLOR = "#caa68f";
const SKIN_DIM_COLOR = "#9b806c";

type Highlight = "primary" | "secondary" | "missed" | "load-under" | "load-on" | "load-over";

const HIGHLIGHT_COLOR = {
  primary: "#3b82f6",
  secondary: "#3b82f6",
  missed: "#ef4444",
  "load-under": "#ef4444",
  "load-on": "#10b981",
  "load-over": "#f59e0b",
} as const satisfies Record<Highlight, string>;

const EMISSIVE_STRENGTH = {
  primary: 0.55,
  secondary: 0.25,
  missed: 0.4,
  "load-under": 0.3,
  "load-on": 0.5,
  "load-over": 0.45,
} as const satisfies Record<Highlight, number>;

function buildNameChain(object: THREE.Object3D): Array<string> {
  const names: Array<string> = [];
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current.name) names.push(current.name);
    current = current.parent;
  }
  return names;
}

const CLASSIFY_CACHE = new Map<string, MuscleClassification>();

function classifyMesh(object: THREE.Object3D): MuscleClassification {
  const joinedName = buildNameChain(object).join(" | ");

  const cached = CLASSIFY_CACHE.get(joinedName);
  if (cached) return cached;

  let result: MuscleClassification = { kind: "hidden" };

  if (HIDE_PATTERNS.some((pattern) => pattern.test(joinedName))) {
    result = { kind: "hidden" };
  } else {
    let matched = false;
    for (const muscleId of ALL_MUSCLE_IDS) {
      const patterns = MUSCLE_NAME_PATTERNS[muscleId];
      if (patterns.some((pattern) => pattern.test(joinedName))) {
        result = { kind: "muscle", muscleId };
        matched = true;
        break;
      }
    }
    if (!matched) {
      result = GENERIC_MUSCLE_PATTERN.test(joinedName) ? { kind: "generic" } : { kind: "hidden" };
    }
  }

  CLASSIFY_CACHE.set(joinedName, result);
  return result;
}

function classifyLimb(joinedName: string): { side: LimbSide; segment: LimbSegment } | null {
  const lastNamePart = joinedName.split("|").pop()?.trim() ?? "";
  const sideMatch = lastNamePart.match(/\.[a-z0-9]*([lr])$/i);
  if (!sideMatch) return null;

  const sideChar = sideMatch[1]?.toLowerCase();
  if (sideChar !== "l" && sideChar !== "r") return null;

  const side: LimbSide = sideChar;

  const isFootMuscle = / of foot|hallucis|metatars|plantar|calcane/i.test(joinedName);
  const isHandMuscle = / of hand|metacarp|hypothenar|thenar|\bpalmar\b/i.test(joinedName);

  let segment: LimbSegment | null = null;
  if (
    !isHandMuscle &&
    /brachioradialis|flexor carpi|extensor carpi|pronator|supinator|anconeus/i.test(joinedName)
  ) {
    segment = "forearm";
  } else if (/brachii|brachialis|coracobrachialis|deltoid/i.test(joinedName)) {
    segment = "upperarm";
  } else if (isHandMuscle || /finger of hand|of hand/i.test(joinedName)) {
    segment = "hand";
  } else if (isFootMuscle || /finger of foot/i.test(joinedName)) {
    segment = "foot";
  } else if (
    /gastrocnemius|\bsoleus\b|tibialis|peroneus|fibularis|popliteus|triceps surae/i.test(joinedName)
  ) {
    segment = "shin";
  } else if (
    /femoris|vastus|sartorius|gracilis|adductor(?! hallucis)|semimembranosus|semitendinosus|pectineus|tensor fasciae/i.test(
      joinedName,
    )
  ) {
    segment = "thigh";
  }

  return segment === null ? null : { side, segment };
}

type LimbGroups = Record<LimbSide, Record<LimbSegment, Array<THREE.Mesh>>>;
type LimbPivots = Partial<Record<string, THREE.Object3D>>;

const buildEmptySegmentMap = (): Record<LimbSegment, Array<THREE.Mesh>> => ({
  upperarm: [],
  forearm: [],
  hand: [],
  thigh: [],
  shin: [],
  foot: [],
});

function buildEmptyGroups(): LimbGroups {
  return { l: buildEmptySegmentMap(), r: buildEmptySegmentMap() };
}

const boxCenterX = (box: THREE.Box3): number => (box.min.x + box.max.x) / 2;
const boxCenterZ = (box: THREE.Box3): number => (box.min.z + box.max.z) / 2;

function computeWorldBox(meshes: ReadonlyArray<THREE.Mesh>): THREE.Box3 {
  const box = new THREE.Box3();
  for (const mesh of meshes) box.union(new THREE.Box3().setFromObject(mesh));
  return box;
}

function rigLimbs(root: THREE.Object3D): LimbPivots {
  let topAncestor: THREE.Object3D = root;
  while (topAncestor.parent) topAncestor = topAncestor.parent;
  topAncestor.updateMatrixWorld(true);

  const groups = buildEmptyGroups();

  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !object.visible) return;

    const chain = buildNameChain(object).join(" | ");
    const limb = classifyLimb(chain);
    if (limb) groups[limb.side][limb.segment].push(object);
  });

  const pivots: LimbPivots = {};

  function buildChain(side: LimbSide, kind: "arm" | "leg"): void {
    const config =
      kind === "arm"
        ? {
            upper: "upperarm" as const,
            lower: "forearm" as const,
            tip: "hand" as const,
            topKey: "Shoulder",
            midKey: "Elbow",
          }
        : {
            upper: "thigh" as const,
            lower: "shin" as const,
            tip: "foot" as const,
            topKey: "Hip",
            midKey: "Knee",
          };

    const upperMeshes = groups[side][config.upper];
    const lowerMeshes = groups[side][config.lower];
    const tipMeshes = groups[side][config.tip];
    if (upperMeshes.length === 0 || lowerMeshes.length === 0) return;

    const upperBox = computeWorldBox(upperMeshes);
    const lowerBox = computeWorldBox(lowerMeshes);
    if (upperBox.isEmpty() || lowerBox.isEmpty()) return;

    const jointTopWorld = new THREE.Vector3(
      boxCenterX(upperBox),
      upperBox.max.y,
      boxCenterZ(upperBox),
    );
    const jointMidWorld = new THREE.Vector3(
      boxCenterX(lowerBox),
      lowerBox.max.y,
      boxCenterZ(lowerBox),
    );

    const outerPivot = new THREE.Object3D();
    outerPivot.name = `pivot_${side}${config.topKey}`;
    root.add(outerPivot);
    root.updateMatrixWorld(true);
    outerPivot.position.copy(root.worldToLocal(jointTopWorld.clone()));
    outerPivot.updateMatrixWorld(true);

    const innerPivot = new THREE.Object3D();
    innerPivot.name = `pivot_${side}${config.midKey}`;
    outerPivot.add(innerPivot);
    outerPivot.updateMatrixWorld(true);
    innerPivot.position.copy(outerPivot.worldToLocal(jointMidWorld.clone()));
    innerPivot.updateMatrixWorld(true);

    for (const mesh of upperMeshes) outerPivot.attach(mesh);
    for (const mesh of [...lowerMeshes, ...tipMeshes]) innerPivot.attach(mesh);

    pivots[side + config.topKey] = outerPivot;
    pivots[side + config.midKey] = innerPivot;
  }

  buildChain("l", "arm");
  buildChain("r", "arm");
  buildChain("l", "leg");
  buildChain("r", "leg");

  return pivots;
}

type AnatomyModelProps = {
  active: MuscleHighlightMap;
  exerciseId: ExerciseId;
  animate?: boolean;
  url?: string;
  onActiveBox?: (box: THREE.Box3 | null) => void;
  /** When provided, renders an HTML label at the center of each muscle. */
  muscleLabels?: Partial<Record<MuscleId, string>>;
};

function isMaterialWithColor(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return "color" in material;
}

function isMeshWithStandardMaterial(
  mesh: THREE.Mesh,
): mesh is THREE.Mesh & { material: THREE.MeshStandardMaterial } {
  return !Array.isArray(mesh.material) && isMaterialWithColor(mesh.material);
}

const DEFAULT_URL = "/zanatomy_muscular.glb";

export function AnatomyModel({
  active,
  exerciseId,
  animate = true,
  url = DEFAULT_URL,
  onActiveBox,
  muscleLabels,
}: AnatomyModelProps): React.JSX.Element {
  const { scene } = useGLTF(url);
  const clonedScene = useMemo(() => scene.clone(true), [scene]);
  const animationRootRef = useRef<THREE.Group>(null);
  const pivotsRef = useRef<LimbPivots>({});

  const muscleMeshesRef = useRef<Map<MuscleId, Array<THREE.Mesh>>>(new Map());
  const genericMeshesRef = useRef<Array<THREE.Mesh>>([]);
  const [muscleCenters, setMuscleCenters] = useState<
    Partial<Record<MuscleId, [number, number, number]>>
  >({});

  const useClassifier = /\b(anatomy|zanatomy)/i.test(url);

  useEffect(() => {
    const muscleMeshes = new Map<MuscleId, Array<THREE.Mesh>>();
    const genericMeshes: Array<THREE.Mesh> = [];

    clonedScene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const mesh = object;

      const classification: MuscleClassification = useClassifier
        ? classifyMesh(mesh)
        : { kind: "generic" };
      const userData: MeshUserData = mesh.userData;
      userData.classification = classification;

      if (classification.kind === "hidden") {
        mesh.visible = false;
        return;
      }

      if (!userData.clonedMaterial && !Array.isArray(mesh.material)) {
        mesh.material = mesh.material.clone();
        userData.clonedMaterial = mesh.material;
      }

      if (classification.kind === "muscle") {
        const bucket = muscleMeshes.get(classification.muscleId) ?? [];
        bucket.push(mesh);
        muscleMeshes.set(classification.muscleId, bucket);
      } else if (classification.kind === "generic") {
        genericMeshes.push(mesh);
      }
    });

    muscleMeshesRef.current = muscleMeshes;
    genericMeshesRef.current = genericMeshes;

    if (useClassifier) {
      pivotsRef.current = rigLimbs(clonedScene);
    }

    clonedScene.updateMatrixWorld(true);
    const centers: Partial<Record<MuscleId, [number, number, number]>> = {};
    for (const [muscleId, meshes] of muscleMeshes) {
      const box = new THREE.Box3();
      let any = false;
      for (const mesh of meshes) {
        const meshBox = new THREE.Box3().setFromObject(mesh);
        if (!meshBox.isEmpty()) {
          box.union(meshBox);
          any = true;
        }
      }
      if (any) {
        const center = new THREE.Vector3();
        box.getCenter(center);
        clonedScene.worldToLocal(center);
        centers[muscleId] = [center.x, center.y, center.z];
      }
    }
    setMuscleCenters(centers);
  }, [clonedScene, useClassifier]);

  useEffect(() => {
    const muscleMeshes = muscleMeshesRef.current;
    const genericMeshes = genericMeshesRef.current;

    const applyHighlight = (
      mesh: THREE.Mesh,
      highlight: Highlight | undefined,
      isGeneric: boolean,
    ): void => {
      if (!isMeshWithStandardMaterial(mesh)) return;

      const material = mesh.material;

      if (highlight) {
        const color = new THREE.Color(HIGHLIGHT_COLOR[highlight]);
        material.color.copy(color);
        if (material.emissive) {
          material.emissive.copy(color).multiplyScalar(EMISSIVE_STRENGTH[highlight]);
        }
        material.opacity = 1.0;
        material.transparent = false;
      } else {
        material.color.set(isGeneric ? SKIN_DIM_COLOR : SKIN_COLOR);
        if (material.emissive) material.emissive.set("#000000");
        material.opacity = isGeneric ? 0.55 : 0.92;
        material.transparent = material.opacity < 1.0;
      }
      material.needsUpdate = true;
    };

    for (const [muscleId, meshes] of muscleMeshes) {
      const highlight = active[muscleId];
      for (const mesh of meshes) applyHighlight(mesh, highlight, false);
    }
    for (const mesh of genericMeshes) {
      applyHighlight(mesh, undefined, true);
    }

    if (!onActiveBox) return;

    const aggregateBox = new THREE.Box3();
    let foundAny = false;

    for (const [muscleId, meshes] of muscleMeshes) {
      if (active[muscleId] !== "primary") continue;
      for (const mesh of meshes) {
        const meshBox = new THREE.Box3().setFromObject(mesh);
        if (!meshBox.isEmpty()) {
          aggregateBox.union(meshBox);
          foundAny = true;
        }
      }
    }

    onActiveBox(foundAny ? aggregateBox : null);
  }, [clonedScene, active, onActiveBox]);

  const { scale, position } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(clonedScene);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const targetHeight = 6.5;
    const uniformScale = size.y > 0 ? targetHeight / size.y : 1;

    return {
      scale: [uniformScale, uniformScale, uniformScale] as const,
      position: [
        -center.x * uniformScale,
        -box.min.y * uniformScale,
        -center.z * uniformScale,
      ] as const,
    };
  }, [clonedScene]);

  useFrame((state) => {
    const root = animationRootRef.current;
    if (!root) return;

    const animation = animate ? getAnimation(exerciseId) : getAnimation("default");
    const joints = animation(state.clock.elapsedTime);
    const uniformScale = scale[1];

    root.position.y = (joints.rootY ?? 0) * uniformScale;
    root.rotation.x = (joints.spine?.x ?? 0) * 0.6;
    root.rotation.y = (joints.spine?.y ?? 0) * 0.5;

    if (!animate) return;

    const pivots = pivotsRef.current;
    const apply = (
      pivot: THREE.Object3D | undefined,
      rotation: { x?: number; y?: number; z?: number } | undefined,
    ): void => {
      if (!pivot) return;
      pivot.rotation.x = rotation?.x ?? 0;
      pivot.rotation.y = rotation?.y ?? 0;
      pivot.rotation.z = rotation?.z ?? 0;
    };

    apply(pivots["lShoulder"], joints.lShoulder);
    apply(pivots["rShoulder"], joints.rShoulder);
    apply(pivots["lElbow"], joints.lElbow);
    apply(pivots["rElbow"], joints.rElbow);
    apply(pivots["lHip"], joints.lHip);
    apply(pivots["rHip"], joints.rHip);
    apply(pivots["lKnee"], joints.lKnee);
    apply(pivots["rKnee"], joints.rKnee);
  });

  const labels: ReadonlyArray<readonly [MuscleId, string]> = muscleLabels
    ? ALL_MUSCLE_IDS.flatMap((muscleId) => {
        const text = muscleLabels[muscleId];
        const center = muscleCenters[muscleId];
        return text && center ? [[muscleId, text] as const] : [];
      })
    : [];

  return (
    <group position={position} scale={scale}>
      <group ref={animationRootRef}>
        <primitive object={clonedScene} />
        {labels.map(([muscleId, text]) => {
          const center = muscleCenters[muscleId];
          if (!center) return null;
          const highlight = active[muscleId];
          const borderColor =
            highlight === "load-on"
              ? "border-emerald-500"
              : highlight === "load-over"
                ? "border-amber-500"
                : "border-destructive";
          const valueColor =
            highlight === "load-on"
              ? "text-emerald-500"
              : highlight === "load-over"
                ? "text-amber-500"
                : "text-destructive";
          return (
            <Html
              key={`label-${muscleId}`}
              position={center}
              center
              distanceFactor={6}
              zIndexRange={[10, 0]}
            >
              <div
                className={cn(
                  "pointer-events-auto inline-flex flex-col items-center gap-0.5 whitespace-nowrap rounded-lg border bg-background/85 px-2 py-1 text-[11px] leading-tight text-foreground backdrop-blur-md [transform:translateZ(0)]",
                  borderColor,
                )}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <span className="font-semibold text-muted-foreground">
                  {MUSCLES[muscleId].label}
                </span>
                <span className={cn("font-bold tabular-nums", valueColor)}>{text}</span>
              </div>
            </Html>
          );
        })}
      </group>
    </group>
  );
}

AnatomyModel.preload = (url: string = DEFAULT_URL): void => {
  useGLTF.preload(url);
};
